import isEqual from 'lodash/isEqual';
import { OUTPUT_SHAPES, canonicalMapperConfig, configFromDraft, draftValidationErrors, draftFromConfig, emptyCaptureDraft, emptyEmitRuleDraft, emptyLookupRowDraft, emptyLookupTableDraft } from '@/utils/mapperConfigDraft';
import { stringifyMapperConfig } from '@/utils/mapperConfigText';
import MapperEmitRuleCard from './MapperEmitRuleCard.vue';
import MapperLiteralField from './MapperLiteralField.vue';
export default {
  name: 'MapperConfigBuilder',
  components: {
    MapperEmitRuleCard,
    MapperLiteralField
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: undefined
    },
    format: {
      type: String,
      required: false,
      default: 'json'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      draft: draftFromConfig(this.value)
    };
  },
  computed: {
    outputShapeItems() {
      return OUTPUT_SHAPES.map(shape => ({
        text: this.$i18n_t(`Bridges.mapperBuilder.input.outputShapes.${shape}`),
        value: shape
      }));
    },
    captureNames() {
      return this.draft ? this.draft.input.captures.map(capture => capture.name).filter(Boolean) : [];
    },
    lookupTableNames() {
      return this.draft ? this.draft.lookups.map(table => table.name).filter(Boolean) : [];
    },
    topicPatternError() {
      const pattern = this.draft && this.draft.input.topicPattern;
      if (!pattern) {
        return [];
      }
      if (pattern.length > 256) {
        return [this.$i18n_t('Bridges.mapperBuilder.input.topicPatternTooLong')];
      }
      try {
        new RegExp(pattern);
        return [];
      } catch (_) {
        return [this.$i18n_t('Bridges.mapperBuilder.input.topicPatternInvalid')];
      }
    },
    draftErrors() {
      return this.draft ? draftValidationErrors(this.draft) : [];
    },
    previewText() {
      return stringifyMapperConfig(this.draft ? configFromDraft(this.draft) : {}, this.format);
    }
  },
  watch: {
    value(nextValue) {
      // Own edits round-trip through the parent, so only rebuild on real external changes.
      if (this.draft && isEqual(canonicalMapperConfig(configFromDraft(this.draft)), canonicalMapperConfig(nextValue))) {
        return;
      }
      this.draft = draftFromConfig(nextValue);
    }
  },
  methods: {
    emitChange() {
      if (!this.draft || this.draftErrors.length > 0) {
        return;
      }
      this.$emit('input', configFromDraft(this.draft));
    },
    setOutputShape(shape) {
      this.draft.output = shape;
      this.emitChange();
    },
    setInputField(field, next) {
      this.draft.input[field] = next === null || next === undefined ? '' : next;
      this.emitChange();
    },
    addCapture() {
      this.draft.input.captures.push(emptyCaptureDraft());
      this.emitChange();
    },
    setCaptureName(capture, next) {
      capture.name = next === null || next === undefined ? '' : next;
      this.emitChange();
    },
    setCaptureIndex(capture, next) {
      const parsed = Number.parseInt(next, 10);
      capture.index = Number.isNaN(parsed) ? next : parsed;
      this.emitChange();
    },
    removeCapture(index) {
      this.draft.input.captures.splice(index, 1);
      this.emitChange();
    },
    addLookupTable() {
      this.draft.lookups.push(emptyLookupTableDraft());
      this.emitChange();
    },
    setLookupName(table, next) {
      table.name = next === null || next === undefined ? '' : next;
      this.emitChange();
    },
    removeLookupTable(index) {
      this.draft.lookups.splice(index, 1);
      this.emitChange();
    },
    addLookupRow(table) {
      table.rows.push(emptyLookupRowDraft());
      this.emitChange();
    },
    setLookupRowKey(row, next) {
      row.key = next === null || next === undefined ? '' : next;
      this.emitChange();
    },
    removeLookupRow(table, rowIndex) {
      table.rows.splice(rowIndex, 1);
      this.emitChange();
    },
    addEmitRule() {
      this.draft.emit.push(emptyEmitRuleDraft(this.draft.output));
      this.emitChange();
    },
    moveEmitRule(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.draft.emit.length) {
        return;
      }
      const [rule] = this.draft.emit.splice(index, 1);
      this.draft.emit.splice(target, 0, rule);
      this.emitChange();
    },
    removeEmitRule(index) {
      this.draft.emit.splice(index, 1);
      this.emitChange();
    }
  }
};